/**
 * Clipboard writes for code running inside the dashboard iframe.
 *
 * Takes the target window/document explicitly: this module's own `window` is the parent
 * page, while the callers (AG Grid tooltips, table export actions, block render JS) live
 * in the iframe, and `navigator.clipboard` is bound to the document that asked for it.
 */

/**
 * Copies `text`, falling back to a hidden textarea + `execCommand` when the async
 * Clipboard API is unavailable or rejects (older Safari, a non-secure origin, or a call
 * the browser doesn't consider user-initiated).
 */
export async function writeTextToClipboardWithFallback(
  targetWindow: Window,
  targetDocument: Document,
  text: string
): Promise<void> {
  try {
    await targetWindow.navigator.clipboard?.writeText(text);
    return;
  } catch {
    // Fall through to execCommand fallback.
  }

  const textarea = targetDocument.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  targetDocument.body.appendChild(textarea);

  textarea.focus();
  textarea.select();

  try {
    const ok = targetDocument.execCommand("copy");
    if (!ok) {
      throw new Error("execCommand copy failed");
    }
  } finally {
    textarea.remove();
  }
}
